import React, { useState, useEffect, useRef } from "react";
import logo from "../../assets/logo.jpg";
import {
  NavLink,
  useNavigate,
} from "react-router-dom";

import {
  FaBell,
  FaBars,
  FaSignOutAlt,
  FaUser,
  FaLock,
  FaTimes,
  FaHome,
  FaChalkboardTeacher,
  FaCalendarAlt,
  FaUsers,
  FaShieldAlt,
  FaEye,
  FaEyeSlash,
  FaRedo,
} from "react-icons/fa";

import { FaUserCircle } from "react-icons/fa";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import "../../Styles/Hrnav.css";

import ApiService from "../../nhrc_apis/ApiService";

const { PasswordAPI } = ApiService;

function HrNav() {

  const [menuOpen, setMenuOpen] =
    useState(false);

  const [dropdownOpen, setDropdownOpen] =
    useState(false);

  const [user, setUser] =
    useState(null);

  const navigate = useNavigate();

  const dropdownRef = useRef(null);

  /* =========================================================
     ✅ NEW — CHANGE PASSWORD MODAL STATES

     Same 2-step flow as Forgot Password:
       Step 1: email → PasswordAPI.forgotPassword(email)
               (backend emails an OTP)
       Step 2: OTP + new password + confirm password, all
               together → PasswordAPI.resetPassword({ email,
               otp, new_password, confirm_password }) in one
               call. No separate verify-OTP step, no reset
               token — matches the confirmed Swagger schema.
     Email is pre-filled from the logged-in HR's own email
     (still editable) since we already know who they are.
  ========================================================= */

  const [showChangePwModal, setShowChangePwModal] = useState(false);
  const [changePwStep, setChangePwStep] = useState(1);

  const [changePwEmail, setChangePwEmail] = useState("");
  const [sendingChangePwOtp, setSendingChangePwOtp] = useState(false);

  const [changePwOtp, setChangePwOtp] = useState("");
  const [changePwNewPassword, setChangePwNewPassword] = useState("");
  const [changePwConfirmPassword, setChangePwConfirmPassword] = useState("");
  const [showChangePwNewPassword, setShowChangePwNewPassword] = useState(false);
  const [resettingChangePw, setResettingChangePw] = useState(false);

  /* =========================================
     ✅ FIXED — was reading "loggedInUser", the
     exact key StudentLogin.js also wrote to.
     That's what caused a student login (in
     any tab) to overwrite this HR nav's data,
     since the "storage" listener below fires
     whenever ANY key changes and this function
     blindly re-read the shared key. Now reads
     "hrLoggedInUser", a key ONLY HrLogin.js
     writes to.
  ========================================= */

  const loadUserFromStorage = () => {

    try {

      const storedUser = JSON.parse(
        localStorage.getItem("hrLoggedInUser")
      );

      if (storedUser) {
        setUser(storedUser);
      }

    } catch (err) {
      console.log("Failed to parse hrLoggedInUser", err);
    }
  };

  useEffect(() => {

    loadUserFromStorage();

    window.addEventListener(
      "userLoggedIn",
      loadUserFromStorage
    );

    window.addEventListener(
      "storage",
      loadUserFromStorage
    );

    return () => {
      window.removeEventListener(
        "userLoggedIn",
        loadUserFromStorage
      );
      window.removeEventListener(
        "storage",
        loadUserFromStorage
      );
    };

  }, []);

  useEffect(() => {

    const handleClickOutside = (event) => {

      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target)
      ) {
        setDropdownOpen(false);
      }
    };

    document.addEventListener(
      "mousedown",
      handleClickOutside
    );

    return () => {

      document.removeEventListener(
        "mousedown",
        handleClickOutside
      );
    };

  }, []);

  /* =========================================
     LOGOUT

     ✅ FIXED — also clears the role-scoped
     "hrLoggedInUser"/"hrMember" keys now, not
     just the old shared "loggedInUser"/"member"
     ones.
  ========================================= */

  const handleLogout = () => {

    localStorage.removeItem("hrLoggedInUser");
    localStorage.removeItem("hrMember");
    localStorage.removeItem("loggedInUser");
    localStorage.removeItem("member");
    localStorage.removeItem("employeeToken");
    localStorage.removeItem("adminToken");
    localStorage.removeItem("hrToken");
    localStorage.removeItem("studentToken");
    localStorage.removeItem("token");

    navigate("/");
  };

  const getUserName = () => {

    if (!user) return "HR";

    const name = user?.full_name;

    const isPlaceholder =
      !name ||
      name.trim() === "" ||
      name.trim().toLowerCase() === "string";

    if (!isPlaceholder) {
      return name;
    }

    return (
      user?.membership_id ||
      user?.email ||
      "HR"
    );
  };

  const getProfileImage = () => {

    const img =
      user?.profile_pic ||
      user?.profilePhoto ||
      user?.photo;

    if (!img) return null;

    if (img.startsWith("http")) {
      return img;
    }

    return `https://nhrc.onrender.com/${img}`;
  };

  /* =========================================================
     ✅ NEW — CHANGE PASSWORD HANDLERS
  ========================================================= */

  const openChangePwModal = () => {
    setShowChangePwModal(true);
    setChangePwStep(1);
    setChangePwEmail(user?.email || "");
    setChangePwOtp("");
    setChangePwNewPassword("");
    setChangePwConfirmPassword("");
  };

  const closeChangePwModal = () => {
    setShowChangePwModal(false);
  };

  /* STEP 1 — POST /password/forgot-password?email=... */
  const handleSendChangePwOtp = async () => {
    if (!changePwEmail.trim()) {
      toast.error("Please enter your email address.");
      return;
    }

    try {
      setSendingChangePwOtp(true);

      await PasswordAPI.forgotPassword(
        changePwEmail.trim().toLowerCase()
      );

      toast.success("OTP sent to your email");

      setChangePwStep(2);
    } catch (err) {
      console.error("CHANGE PASSWORD - SEND OTP ERROR:", err);

      toast.error(
        err?.response?.data?.detail ||
          "Failed to send OTP. Please check the email and try again."
      );
    } finally {
      setSendingChangePwOtp(false);
    }
  };

  /* STEP 2 — POST /password/reset-password
     { email, otp, new_password, confirm_password }
     (same single-call object, no separate verify step) */
  const handleChangePassword = async () => {
    if (!changePwOtp.trim()) {
      toast.error("Please enter the OTP.");
      return;
    }

    if (!changePwNewPassword.trim() || !changePwConfirmPassword.trim()) {
      toast.error("Please fill both password fields.");
      return;
    }

    if (changePwNewPassword.length < 6) {
      toast.error("Password must be at least 6 characters.");
      return;
    }

    if (changePwNewPassword !== changePwConfirmPassword) {
      toast.error("Passwords do not match.");
      return;
    }

    try {
      setResettingChangePw(true);

      await PasswordAPI.resetPassword({
        email: changePwEmail.trim().toLowerCase(),
        otp: changePwOtp.trim(),
        new_password: changePwNewPassword.trim(),
        confirm_password: changePwConfirmPassword.trim(),
      });

      toast.success("Password changed successfully!");

      closeChangePwModal();
    } catch (err) {
      console.error("CHANGE PASSWORD - RESET ERROR:", err);

      toast.error(
        err?.response?.data?.detail || "Failed to change password"
      );
    } finally {
      setResettingChangePw(false);
    }
  };

  return (

    <nav className="adminnav-container">

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        pauseOnHover
        draggable
      />

      <div className="adminnav-left-group">

        <div className="adminnav-logo">
          <div className="adminnav-logo-box">
            <img
              src={logo}
              alt="NHRC Logo"
              className="adminnav-logo-img"
            />
          </div>
          <span className="adminnav-logo-label">
            National Human Resource Club (NHRC)
          </span>
        </div>

        <div className="adminnav-left">
          <h4 className="adminnav-title">
            HR: {getUserName()}
          </h4>
        </div>

      </div>

      <button
        className="adminnav-toggler"
        onClick={() =>
          setMenuOpen(!menuOpen)
        }
      >

        {
          menuOpen
            ? <FaTimes />
            : <FaBars />
        }

      </button>

      <div
        className={`adminnav-center ${
          menuOpen ? "open" : ""
        }`}
      >

        <NavLink
          to="/hrdashboard"
          className="adminnav-link"
          onClick={() => setMenuOpen(false)}
        >
          <FaHome className="adminnav-link-icon" />
          Home
        </NavLink>

        <NavLink
          to="/hrdashboard/TrainingProgram"
          className="adminnav-link"
          onClick={() => setMenuOpen(false)}
        >
          <FaChalkboardTeacher className="adminnav-link-icon" />
          Training Program
        </NavLink>

        <NavLink
          to="/hrdashboard/Hrevents"
          className="adminnav-link"
          onClick={() => setMenuOpen(false)}
        >
          <FaCalendarAlt className="adminnav-link-icon" />
          Events
        </NavLink>

        <NavLink
          to="/hrdashboard/Hrjobfairs"
          className="adminnav-link"
          onClick={() => setMenuOpen(false)}
        >
          <FaUsers className="adminnav-link-icon" />
          Job Fairs
        </NavLink>

        <NavLink
          to="/hrdashboard/Blackprofile"
          className="adminnav-link"
          onClick={() => setMenuOpen(false)}
        >
          <FaShieldAlt className="adminnav-link-icon" />
          Black Profile
        </NavLink>

      </div>

      <div className="adminnav-right">

        <FaBell className="adminnav-right-icon" />

        <div
          className="adminnav-profile"
          ref={dropdownRef}
        >

          {
            getProfileImage() ? (

              <img
                src={getProfileImage()}
                alt="profile"
                className="adminnav-profile-img"
                onClick={() =>
                  setDropdownOpen(!dropdownOpen)
                }
              />

            ) : (

              <FaUserCircle
                className="adminnav-profile-img"
                onClick={() =>
                  setDropdownOpen(!dropdownOpen)
                }
              />

            )
          }

          {
            dropdownOpen && (

              <div className="adminnav-dropdown">

                <button
                  onClick={() => {

                    navigate(
                      "/hrdashboard/HremployeeRegistration",
                      {
                        state: {
                          membershipId:
                            user?.membership_id,

                          fullData: user,
                        },
                      }
                    );

                    setDropdownOpen(false);
                  }}
                >

                  <FaUser />

                  My Profile

                </button>

                {/* =========================================
                   ✅ FIXED — was a dead button with no
                   onClick at all. Now opens the Change
                   Password modal (same 2-step flow as
                   Forgot Password).
                ========================================= */}
                <button
                  onClick={() => {
                    openChangePwModal();
                    setDropdownOpen(false);
                  }}
                >

                  <FaLock />

                  Change Password

                </button>

                <button
                  className="logout"
                  onClick={handleLogout}
                >

                  <FaSignOutAlt />

                  Logout

                </button>

              </div>
            )
          }

        </div>

      </div>

      {/* =========================================================
          ✅ NEW — CHANGE PASSWORD MODAL

          Same 2-step pattern as the Forgot Password modals on
          Login.js / HrLogin.js / StudentLogin.js:
            Step 1: email → Send OTP
            Step 2: OTP + New Password + Confirm Password on
                    one screen → one call to reset-password.
          Uses inline styles so it works with zero extra CSS —
          rename/restyle the "hrchangepw-*" classes in Hrnav.css
          if you want it to match the rest of the page exactly.
      ========================================================= */}

      {showChangePwModal && (
        <div
          className="hrchangepw-modal-overlay"
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0,0,0,0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 2000,
          }}
        >
          <div
            className="hrchangepw-modal-card"
            style={{
              background: "#fff",
              borderRadius: "12px",
              padding: "28px",
              width: "100%",
              maxWidth: "400px",
              position: "relative",
            }}
          >
            <button
              type="button"
              className="hrchangepw-modal-close"
              onClick={closeChangePwModal}
              style={{
                position: "absolute",
                top: "12px",
                right: "12px",
                background: "none",
                border: "none",
                fontSize: "18px",
                cursor: "pointer",
              }}
              aria-label="Close"
            >
              <FaTimes />
            </button>

            <h4 style={{ marginBottom: "8px" }}>Change Password</h4>

            {/* ============ STEP 1: EMAIL ============ */}
            {changePwStep === 1 && (
              <>
                <p style={{ fontSize: "14px", color: "#666", marginBottom: "16px" }}>
                  Confirm your email address and we'll send you an OTP.
                </p>

                <input
                  type="email"
                  placeholder="Enter your email"
                  value={changePwEmail}
                  onChange={(e) => setChangePwEmail(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    marginBottom: "16px",
                    border: "1px solid #ccc",
                    borderRadius: "8px",
                  }}
                />

                <button
                  type="button"
                  disabled={sendingChangePwOtp}
                  onClick={handleSendChangePwOtp}
                  style={{
                    width: "100%",
                    padding: "10px",
                    border: "none",
                    borderRadius: "8px",
                    background: "#4b3df5",
                    color: "#fff",
                    fontWeight: 600,
                    cursor: "pointer",
                  }}
                >
                  {sendingChangePwOtp ? "Sending OTP..." : "Send OTP"}
                </button>
              </>
            )}

            {/* ============ STEP 2: OTP + NEW PASSWORD ============ */}
            {changePwStep === 2 && (
              <>
                <p style={{ fontSize: "14px", color: "#666", marginBottom: "16px" }}>
                  Enter the OTP sent to <b>{changePwEmail}</b> and your new password.
                </p>

                <input
                  type="text"
                  placeholder="Enter OTP"
                  value={changePwOtp}
                  onChange={(e) => setChangePwOtp(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    marginBottom: "12px",
                    border: "1px solid #ccc",
                    borderRadius: "8px",
                  }}
                />

                <div style={{ position: "relative", marginBottom: "12px" }}>
                  <input
                    type={showChangePwNewPassword ? "text" : "password"}
                    placeholder="New password"
                    value={changePwNewPassword}
                    onChange={(e) => setChangePwNewPassword(e.target.value)}
                    style={{
                      width: "100%",
                      padding: "10px 40px 10px 12px",
                      border: "1px solid #ccc",
                      borderRadius: "8px",
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowChangePwNewPassword(!showChangePwNewPassword)}
                    style={{
                      position: "absolute",
                      right: "10px",
                      top: "10px",
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                    }}
                  >
                    {showChangePwNewPassword ? <FaEyeSlash /> : <FaEye />}
                  </button>
                </div>

                <input
                  type={showChangePwNewPassword ? "text" : "password"}
                  placeholder="Confirm new password"
                  value={changePwConfirmPassword}
                  onChange={(e) => setChangePwConfirmPassword(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    marginBottom: "16px",
                    border: "1px solid #ccc",
                    borderRadius: "8px",
                  }}
                />

                <button
                  type="button"
                  disabled={resettingChangePw}
                  onClick={handleChangePassword}
                  style={{
                    width: "100%",
                    padding: "10px",
                    marginBottom: "8px",
                    border: "none",
                    borderRadius: "8px",
                    background: "#4b3df5",
                    color: "#fff",
                    fontWeight: 600,
                    cursor: "pointer",
                  }}
                >
                  {resettingChangePw ? "Changing..." : "Change Password"}
                </button>

                <button
                  type="button"
                  disabled={sendingChangePwOtp}
                  onClick={handleSendChangePwOtp}
                  style={{
                    width: "100%",
                    padding: "10px",
                    border: "1px solid #ccc",
                    borderRadius: "8px",
                    background: "#fff",
                    color: "#333",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "6px",
                  }}
                >
                  <FaRedo />
                  Resend OTP
                </button>
              </>
            )}

          </div>
        </div>
      )}

    </nav>
  );
}

export default HrNav;